import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  options: readonly SelectOption[];
  disabled?: boolean;
  /** Guidance shown under the field. A validation error replaces it, as on `TextField`. */
  helperText?: string;
}

/** The reusable dropdown ("Select") field (docs/development/FrontendGuidelines.md §8.2). */
export function SelectField<T extends FieldValues>({
  name,
  control,
  label,
  options,
  disabled,
  helperText,
}: SelectFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth size="small" error={Boolean(fieldState.error)} disabled={disabled}>
          <InputLabel id={`${name}-label`}>{label}</InputLabel>
          <Select {...field} labelId={`${name}-label`} label={label}>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {fieldState.error?.message ?? helperText ? (
            <FormHelperText>{fieldState.error?.message ?? helperText}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
}
